// The public pages as HTML strings. No DOM, no Firebase: the browser renders
// from here (site.js), and so does the build, which writes each route's full
// HTML to a static file (vite-seo.js). One renderer, so the prerendered page
// and the live one cannot drift -- and when they match exactly, the browser
// keeps the prerendered DOM instead of replacing it.
//
// Behaviour (the carousel, the contact form) is not here; site.js wires it
// onto whichever DOM ends up on the page.
import { esc, ph } from '../lib/dom.js';
import { LOGO } from '../lib/logo.js';
import { uploadedAt } from '../lib/image-name.js';
import { visiblePages, isHidden } from '../lib/pages.js';

const logo = (alt) => `<img src="${LOGO}" alt="${esc(alt)}" />`;

function header(active, settings) {
  return `
    <header class="site-head">
      <div class="inner">
        <a class="brand" href="/">${logo(settings.name)}
          <span class="brand-text">
            <span class="brand-name">${esc(settings.name)}</span>
            <span class="brand-tag">${esc(settings.tagline)}</span>
          </span>
        </a>
        <nav class="site-nav">
          ${visiblePages(settings)
            .map(
              (p) =>
                `<a href="${p.path}">${esc(p.label)}${p.id === active ? '<span class="bar"></span>' : ''}</a>`,
            )
            .join('')}
        </nav>
      </div>
    </header>
  `;
}

function footer(settings) {
  return `
    <footer class="site-foot">
      <div class="inner">
        <div>${logo(settings.name)}
          <div class="fname">${esc(settings.name)}</div>
          <div class="ftag">${esc(settings.tagline)}</div>
        </div>
        <div>
          <div class="fhead">Pages</div>
          <div class="flinks">
            ${visiblePages(settings).map((p) => `<a href="${p.path}">${esc(p.label)}</a>`).join('')}
          </div>
        </div>
        <div>
          <div class="fhead">Find us</div>
          <div class="faddr">${settings.footerAddressLines.map(esc).join('<br />')}</div>
          <a href="${esc(settings.instagramUrl)}" target="_blank" rel="noopener" style="display:inline-block;margin-top:12px">Instagram &#8594;</a>
        </div>
        <div>
          <div class="fhead">Certified</div>
          <div class="faddr">${esc(settings.certification)}<br />Quality Management System</div>
        </div>
      </div>
      <div class="fbar"><div class="inner">${esc(settings.copyright)}</div></div>
    </footer>
  `;
}

/* ---------------- home ---------------- */

function home({ home, founders, settings }) {
  const slides = home.slides ?? [];

  // Drop a call to action whose destination is hidden, and promote the
  // survivor so the hero never loses its primary button.
  const ctas = [
    { page: 'camps', path: '/camps', label: home.primaryCta },
    { page: 'events', path: '/events', label: home.secondaryCta },
  ].filter((c) => !isHidden(settings, c.page));
  const ctaMarkup = ctas
    .map(
      (c, i) =>
        `<a class="${i === 0 ? 'btn-solid' : 'btn-out'}" href="${c.path}">${esc(c.label)}</a>`,
    )
    .join('');

  // The first slide's caption and dot are drawn here rather than left for
  // the carousel script, so the prerendered page is complete without it.
  return `
    <div>
      <section class="inner hero">
        <div>
          <div class="badge"><i></i>${esc(home.badge)}</div>
          <h1>${esc(home.title).replaceAll('\n', '<br />')}</h1>
          <p class="lead">${esc(home.lead)}</p>
          <div class="hero-cta">${ctaMarkup}</div>
        </div>
        <div class="carousel-wrap">
          <div class="carousel">
            <div class="carousel-track">
              ${slides.map((s) => ph(s, { className: 'carousel-slide' })).join('')}
            </div>
            <div class="carousel-cap"><b>${esc(slides[0]?.title)}</b><span>${esc(slides[0]?.caption)}</span></div>
            <button class="carousel-nav prev" type="button" aria-label="Previous slide">&#8249;</button>
            <button class="carousel-nav next" type="button" aria-label="Next slide">&#8250;</button>
          </div>
          <div class="dots">
            ${slides.map((_, i) => `<button type="button" data-i="${i}" aria-label="Slide ${i + 1}">${i === 0 ? '<i></i>' : ''}</button>`).join('')}
          </div>
        </div>
      </section>

      <section class="pillars">
        <div class="inner">
          ${(home.pillars ?? [])
            .map(
              (p, i) =>
                `<div class="pillar" style="animation-delay:${i * 0.1}s"><b>${esc(p.title)}</b><span>${esc(p.body)}</span></div>`,
            )
            .join('')}
        </div>
      </section>

      <section class="inner" style="padding-top:80px;padding-bottom:80px">
        <h2 class="h2">What we do</h2>
        <div class="cards">
          ${(home.work ?? [])
            .map(
              (w) =>
                `<div class="card"><div class="rule"></div><h3>${esc(w.title)}</h3><p>${esc(w.body)}</p></div>`,
            )
            .join('')}
        </div>
      </section>

      <section class="band">
        <div class="inner">
          <h2 class="h2">The founders</h2>
          <div class="founders">
            ${(founders.items ?? [])
              .map(
                (f) => `
              <div class="founder">
                ${ph(f, { hintKey: 'photoHint' })}
                <div class="body">
                  <h3>${esc(f.name)}</h3>
                  <div class="role">${esc(f.role)}</div>
                  <p>${esc(f.bio)}</p>
                </div>
              </div>`,
              )
              .join('')}
          </div>
        </div>
      </section>

      <section class="inner" style="padding-top:80px;padding-bottom:80px">
        <div class="section-head">
          <div>
            <h2 class="h2" style="margin-bottom:8px">From the field</h2>
            <p style="font-size:16px;color:var(--ink-4);margin:0">Latest from our Instagram.</p>
          </div>
          <a href="${esc(settings.instagramUrl)}" target="_blank" rel="noopener" style="font-weight:600;font-size:15px">&#64;${esc(settings.instagram)} &#8594;</a>
        </div>
        <div class="grid-ig">
          ${(home.instagramPosts ?? []).map((g) => ph(g)).join('')}
        </div>
      </section>
    </div>
  `;
}

/* ---------------- camps ---------------- */

function camps({ camps }) {
  return `
      <div class="inner pad rise">
        <h1 class="page-title">Health camps</h1>
        <p class="page-lead">${esc(camps.lead)}</p>
        <div style="display:flex;flex-direction:column;gap:20px">
          ${(camps.items ?? [])
            .map(
              (c) => `
            <article class="camp">
              <div class="main">
                <div class="row-wrap">
                  <span class="pill">${esc(c.status)}</span>
                  <span class="when">${esc(c.date)}</span>
                </div>
                <div>
                  <h2>${esc(c.village)}</h2>
                  <div class="venue">${esc(c.venue)}</div>
                </div>
                <div>
                  <div class="eyebrow">Services</div>
                  <div class="chips">${(c.services ?? []).map((s) => `<span class="chip">${esc(s)}</span>`).join('')}</div>
                </div>
                <div>
                  <div class="eyebrow">Specialists attending</div>
                  <div style="font-size:15px;color:var(--ink-2);line-height:1.6">${esc(c.specialists)}</div>
                </div>
              </div>
              <div class="side">
                <div class="ph map"><span>map &#8212; ${esc(c.village)}\n${esc(c.coords)}</span></div>
                ${ph(c, { hintKey: 'photoHint' })}
              </div>
            </article>`,
            )
            .join('')}
        </div>
      </div>
    `;
}

/* ---------------- events ---------------- */

function events({ events }, eventId) {
  const items = events.items ?? [];
  const current = eventId ? items.find((e) => e.id === eventId) : null;

  if (current) {
    return `
        <div class="inner pad rise">
          <a href="/events" style="font-size:14.5px;font-weight:600">&#8592; All events</a>
          <h1 class="page-title" style="margin-top:18px;font-size:clamp(32px,4.2vw,50px)">${esc(current.title)}</h1>
          <div class="row-wrap" style="margin-bottom:22px">
            <span class="pill">${esc(current.status)}</span>
            <span class="when" style="font-size:14.5px">${esc(current.date)}</span>
            <span style="font-size:14.5px;color:var(--ink-4)">${esc(current.place)}</span>
          </div>
          <p style="font-size:18px;line-height:1.65;color:var(--ink-2);max-width:66ch;margin:0 0 40px;text-wrap:pretty">${esc(current.detail)}</p>
          <div class="photo-grid">
            ${(current.photos ?? []).map((p) => ph(typeof p === 'string' ? { hint: p } : p)).join('')}
          </div>
        </div>
      `;
  }

  return `
      <div class="inner pad rise">
        <h1 class="page-title">Events</h1>
        <p class="page-lead" style="margin-bottom:40px">${esc(events.lead)}</p>
        <div class="events">
          ${items
            .map(
              (e) => `
            <a class="event" href="/events/${esc(e.id)}">
              ${ph({ ...e, hint: e.photoHint ?? `cover photo — ${e.title.toLowerCase()}` })}
              <div class="body">
                <div class="row-wrap" style="margin-bottom:12px">
                  <span class="pill">${esc(e.status)}</span>
                  <span class="when" style="font-size:13.5px">${esc(e.date)}</span>
                </div>
                <h2>${esc(e.title)}</h2>
                <p>${esc(e.blurb)}</p>
                <span class="count">${(e.photos ?? []).length} photos</span>
              </div>
            </a>`,
            )
            .join('')}
        </div>
      </div>
    `;
}

/* ---------------- gallery ---------------- */

// Every image uploaded anywhere on the site, gathered here so the gallery is
// not a second list to maintain by hand. Sources that are switched off still
// contribute -- hiding a page hides the page, not the photographs.
function collectImages({ gallery, events, camps, cultural, founders, home }) {
  const found = [];
  const add = (image, caption) => {
    if (image) found.push({ image, caption, at: uploadedAt(image) });
  };

  (gallery.items ?? []).forEach((g) => add(g.image, g.caption));

  (events.items ?? []).forEach((e) => {
    add(e.image, e.title);
    (e.photos ?? []).forEach((photo) => {
      if (typeof photo === 'string') return;
      add(photo.image, photo.hint || `${e.title} · ${e.date}`);
    });
  });

  (camps.items ?? []).forEach((c) => add(c.image, `${c.village} · ${c.date}`));
  (cultural.items ?? []).forEach((c) => add(c.image, c.title));
  (founders.items ?? []).forEach((f) => add(f.image, f.name));
  (home.slides ?? []).forEach((s) => add(s.image, s.title));
  (home.instagramPosts ?? []).forEach((g) => add(g.image, g.hint));

  // The same file used in two places should appear once.
  const seen = new Set();
  const unique = found.filter((item) => !seen.has(item.image) && seen.add(item.image));

  // Newest upload first. Anything not named by our uploader has no time and
  // keeps its original order behind the dated ones.
  return unique.sort((a, b) => b.at - a.at);
}

function gallery(data) {
  const all = collectImages(data);
  const grid = all.length
    ? `<div class="gallery">${all
        // Captions double as alt text: the hint is placeholder copy, the
        // caption says what the photograph is.
        .map((g) => `<figure>${ph({ ...g, hint: g.caption })}<figcaption>${esc(g.caption ?? '')}</figcaption></figure>`)
        .join('')}</div>`
    : '<p class="page-lead" style="margin:0">No photographs yet. Images uploaded anywhere in the admin area appear here.</p>';

  return `
    <div class="inner pad rise">
      <h1 class="page-title">Gallery</h1>
      <p class="page-lead" style="margin-bottom:32px">${esc(data.gallery.lead)}</p>
      ${grid}
    </div>
  `;
}

/* ---------------- cultural ---------------- */

function cultural({ cultural }) {
  return `
      <div class="rise">
        <div class="inner" style="padding-top:64px;padding-bottom:40px">
          <h1 class="page-title">Sports &amp; culture</h1>
          <p class="page-lead" style="margin-bottom:0">${esc(cultural.lead)}</p>
        </div>
        <div class="inner cultural" style="padding-bottom:88px">
          ${(cultural.items ?? [])
            .map(
              (c) => `
            <div class="cult">
              ${ph(c, { hintKey: 'photoHint' })}
              <div class="body">
                <div class="kind">${esc(c.kind)}</div>
                <h2>${esc(c.title)}</h2>
                <p>${esc(c.body)}</p>
              </div>
            </div>`,
            )
            .join('')}
        </div>
      </div>
    `;
}

/* ---------------- contact ---------------- */

function contact({ contact, settings }) {
  return `
    <div class="inner pad contact rise">
      <div>
        <h1 class="page-title">Contact us</h1>
        <p class="page-lead" style="margin-bottom:36px">${esc(contact.lead)}</p>
        <div class="contact-info">
          <div>
            <div class="eyebrow">Registered address</div>
            <div class="val">${settings.addressLines.map(esc).join('<br />')}</div>
          </div>
          <div>
            <div class="eyebrow">Phone &amp; email</div>
            <div class="mono">${esc(settings.phone || '[phone — add in admin]')}<br />${esc(settings.email || '[email — add in admin]')}</div>
          </div>
          <div>
            <div class="eyebrow">Instagram</div>
            <a href="${esc(settings.instagramUrl)}" target="_blank" rel="noopener" style="font-size:16px;font-weight:600">&#64;${esc(settings.instagram)}</a>
          </div>
          <div class="cert">
            <div class="eyebrow" style="margin-bottom:12px">Certification</div>
            <div class="cert-row">
              <img src="/assets/iso-certificate.jpg" alt="ISO 9001:2015 certificate" onerror="this.remove()" />
              <p>${esc(settings.certificationNote)}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="form-card">
        <h2>Send a message</h2>
        <form novalidate>
          <label>Your name
            <input name="name" type="text" placeholder="Full name" required />
          </label>
          <label>Phone or email
            <input name="contact" type="text" placeholder="How we reach you" required />
          </label>
          <label>Reason
            <select name="reason">
              ${(contact.reasons ?? []).map((r) => `<option>${esc(r)}</option>`).join('')}
            </select>
          </label>
          <label>Message
            <textarea name="message" rows="5" placeholder="Tell us a little more"></textarea>
          </label>
          <button type="submit">Send message</button>
          <div class="note ok" hidden>${esc(contact.thanks)}</div>
          <div class="note bad" hidden></div>
        </form>
      </div>
    </div>
  `;
}

/* ---------------- entry ---------------- */

// The content blocks each page reads.
export const NEEDS = {
  home: ['settings', 'home', 'founders'],
  camps: ['settings', 'camps'],
  events: ['settings', 'events'],
  gallery: ['settings', 'gallery', 'events', 'camps', 'cultural', 'founders', 'home'],
  cultural: ['settings', 'cultural'],
  contact: ['settings', 'contact'],
};

const BODY = { home, camps, events, gallery, cultural, contact };

// The whole page, header to footer, as one trimmed string.
export function renderPage({ page = 'home', eventId = null, data }) {
  return `<div class="shell">${header(page, data.settings)}<main>${BODY[page](data, eventId)}</main>${footer(data.settings)}</div>`;
}
